/**
 * Where everything sits on the table, in design units.
 *
 * The canvas draws from these rectangles, the hit layer lays its buttons over
 * them and the tooltip points at them — one source, so a click can never land
 * beside the thing it is for. Pure, so what fits can be tested without a DOM.
 *
 * The table is a fixed design space scaled to fit the window: the board across
 * the top, the prompt bar under it, and the tableau of the seat on show along
 * the bottom — the mat on the left, everything that seat holds to its right.
 */
import {
  MANIFESTS,
  type PlayerCount,
  type PlayerId,
  type PlayerView,
  type View,
} from '@trajan/engine';
import { packPitch } from '../layout.js';
import {
  MARKER,
  MAT_H,
  MAT_W,
  SLOT_H,
  SLOT_W,
  TRAY_H,
  TRAY_W,
  slotCentre,
  trayCentre,
  trayRow,
  type MarkerRow,
} from '../mat/geometry.js';
import type { TargetId } from './ids.js';

export const TABLE_W = 1320;
export const TABLE_H = 1000;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export const centreOf = (r: Rect): Point => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });

// ---------------------------------------------------------------------------
// The board and its areas
// ---------------------------------------------------------------------------

export const BOARD: Rect = { x: 10, y: 8, w: 1300, h: 584 };
/** Height of the title band along the top of every area. */
export const AREA_TITLE_H = 30;

export const TIME: Rect = { x: 24, y: 20, w: 1272, h: 46 };
export const FORUM: Rect = { x: 24, y: 74, w: 300, h: 296 };
export const MAP: Rect = { x: 332, y: 74, w: 548, h: 296 };
export const DISTRICT: Rect = { x: 888, y: 74, w: 408, h: 296 };
export const SEAPORT: Rect = { x: 24, y: 378, w: 300, h: 202 };
export const STACKS: Rect = { x: 332, y: 378, w: 548, h: 202 };
export const SENATE: Rect = { x: 888, y: 378, w: 408, h: 202 };

export const PROMPT: Rect = { x: 10, y: 598, w: 1300, h: 44 };
export const TABLEAU: Rect = { x: 10, y: 648, w: 1300, h: 344 };

// --- time track -------------------------------------------------------------

/** The strip of the time track the stones sit on, between the round and the demands. */
export const TIME_TRACK: Rect = { x: 236, y: TIME.y, w: 770, h: TIME.h };
export const TIME_LABEL: Rect = { x: TIME.x, y: TIME.y, w: 204, h: TIME.h };

/**
 * The `k`th stone of the lap, counted from the start space — so the marker walks
 * left to right through a round and is back at the left when the next begins.
 */
export function timeStone(k: number, length: number): Point & { r: number } {
  const pitch = TIME_TRACK.w / length;
  return {
    x: TIME_TRACK.x + pitch * (k + 0.5),
    y: TIME_TRACK.y + TIME_TRACK.h / 2,
    r: Math.min(15, pitch * 0.42),
  };
}

/** Where on the lap, counted from the start space, the time marker stands. */
export const lapStep = (time: Pick<View['time'], 'position' | 'start' | 'length'>): number =>
  (((time.position - time.start) % time.length) + time.length) % time.length;

export const DEMAND_TILE = 42;
export function demandSlot(i: number): Rect {
  return {
    x: TIME.x + TIME.w - 3 * DEMAND_TILE - 2 * 8 + i * (DEMAND_TILE + 8),
    y: TIME.y + (TIME.h - DEMAND_TILE) / 2,
    w: DEMAND_TILE,
    h: DEMAND_TILE,
  };
}

// --- forum ---------------------------------------------------------------------

export const FORUM_TILE = 56;
const FORUM_GAP = 8;
const FORUM_COLUMNS = 4;
const forumLeft = FORUM.x + (FORUM.w - (FORUM_COLUMNS * FORUM_TILE + 3 * FORUM_GAP)) / 2;

export function forumSpace(i: number): Rect {
  const col = i % FORUM_COLUMNS;
  const row = Math.floor(i / FORUM_COLUMNS);
  return {
    x: forumLeft + col * (FORUM_TILE + FORUM_GAP),
    y: FORUM.y + AREA_TITLE_H + 6 + row * (FORUM_TILE + FORUM_GAP),
    w: FORUM_TILE,
    h: FORUM_TILE,
  };
}

/** The three yellow extra-action spaces, in a row under the forum, beside their caption. */
export function yellowSpace(i: number): Rect {
  return {
    x: forumLeft + (i + 1) * (FORUM_TILE + FORUM_GAP),
    y: FORUM.y + FORUM.h - FORUM_TILE - 10,
    w: FORUM_TILE,
    h: FORUM_TILE,
  };
}

export const YELLOW_CAPTION: Rect = {
  x: forumLeft,
  y: FORUM.y + FORUM.h - FORUM_TILE - 10,
  w: FORUM_TILE,
  h: FORUM_TILE,
};

// --- the province map ---------------------------------------------------------

/** The parchment the map is drawn on. */
export const MAP_INSET: Rect = {
  x: MAP.x + 8,
  y: MAP.y + AREA_TITLE_H + 2,
  w: MAP.w - 16,
  h: MAP.h - AREA_TITLE_H - 10,
};

/**
 * Province medallions, roughly where the provinces lie — Britannia over the
 * water to the north-west, Italia at the bottom right — so the map is easy to
 * learn. The roads between them are the rules; the geography is only a guide.
 * Positions are from the parchment's top-left corner.
 */
const PROVINCE_AT: Record<string, Point> = {
  britannia: { x: 80, y: 40 },
  'germania-inferior': { x: 400, y: 36 },
  belgica: { x: 262, y: 84 },
  'germania-superior': { x: 454, y: 102 },
  lugdunensis: { x: 140, y: 128 },
  raetia: { x: 358, y: 148 },
  noricum: { x: 470, y: 178 },
  alpes: { x: 252, y: 186 },
  aquitania: { x: 62, y: 190 },
  narbonensis: { x: 160, y: 212 },
};
const ITALIA_AT: Point = { x: 376, y: 214 };

export const MEDALLION_R = 20;
/** The hit area of a province: its medallion and the pieces standing either side. */
const NODE_W = 88;
const NODE_H = 60;

export function provinceCentre(id: string): Point {
  const at = id === 'italia' ? ITALIA_AT : PROVINCE_AT[id];
  if (!at) throw new Error(`no position for province ${id}`);
  return { x: MAP_INSET.x + at.x, y: MAP_INSET.y + at.y };
}

export function provinceRect(id: string): Rect {
  const c = provinceCentre(id);
  return { x: c.x - NODE_W / 2, y: c.y - NODE_H / 2, w: NODE_W, h: NODE_H };
}

/**
 * Where the pieces in a province stand: its legionnaires in a column to the
 * left of the medallion, leaders to the right, up to four of each — one per
 * player is the most the rules allow of either. `k` counts from the top.
 */
export function provincePiece(id: string, side: 'left' | 'right', k: number, of: number): Point {
  const c = provinceCentre(id);
  const pitch = packPitch(of, 34, 12);
  const top = c.y - ((of - 1) * pitch) / 2;
  return { x: c.x + (side === 'left' ? -31 : 31), y: top + k * pitch };
}

/** The forum tile a province holds until a leader seizes it: on the medallion's shoulder. */
export function provinceTile(id: string): Rect {
  const c = provinceCentre(id);
  return { x: c.x + 9, y: c.y - MEDALLION_R - 9, w: 22, h: 22 };
}

// --- construction district ----------------------------------------------------

const SITE_GAP = 6;
export const siteRows = (playerCount: PlayerCount): number =>
  MANIFESTS.constructionGrid.rowsByPlayerCount[playerCount];

export function siteRect(i: number, rows: number): Rect {
  const cols = MANIFESTS.constructionGrid.columns;
  const w = (DISTRICT.w - 30 - (cols - 1) * SITE_GAP) / cols;
  const room = DISTRICT.h - AREA_TITLE_H - 12;
  const h = Math.min(62, (room - (rows - 1) * SITE_GAP) / rows);
  const col = i % cols;
  const row = Math.floor(i / cols);
  return {
    x: DISTRICT.x + 15 + col * (w + SITE_GAP),
    y: DISTRICT.y + AREA_TITLE_H + 4 + row * (h + SITE_GAP),
    w,
    h,
  };
}

// --- seaport -------------------------------------------------------------------

export const SHIP_W = 88;
export const SHIP_H = 94;
export function shipRect(i: number): Rect {
  return {
    x: SEAPORT.x + 12 + i * (SHIP_W + 6),
    y: SEAPORT.y + AREA_TITLE_H + 4,
    w: SHIP_W,
    h: SHIP_H,
  };
}

export const PILE_W = 46;
export const PILE_H = 62;
const pilesTop = SEAPORT.y + SEAPORT.h - PILE_H - 8;
export const DECK: Rect = { x: SEAPORT.x + 20, y: pilesTop, w: PILE_W, h: PILE_H };
export function pileRect(i: number): Rect {
  return { x: SEAPORT.x + 150 + i * (PILE_W + 12), y: pilesTop, w: PILE_W, h: PILE_H };
}

// --- Trajan stacks ------------------------------------------------------------

export const STACK_TILE = 74;
export function stackRect(i: number): Rect {
  const gap = 14;
  const left = STACKS.x + (STACKS.w - (6 * STACK_TILE + 5 * gap)) / 2;
  return {
    x: left + i * (STACK_TILE + gap),
    y: STACKS.y + AREA_TITLE_H + 14,
    w: STACK_TILE,
    h: STACK_TILE,
  };
}

// --- senate --------------------------------------------------------------------

const STEP_W = 44;
const STEP_GAP = 4;
const stepsBottom = SENATE.y + AREA_TITLE_H + 88;
export function senateStep(i: number): Rect {
  const h = 46 + i * 6;
  const count = MANIFESTS.senateTrack.spaces.length;
  const left = SENATE.x + (SENATE.w - (count * STEP_W + (count - 1) * STEP_GAP)) / 2;
  return { x: left + i * (STEP_W + STEP_GAP), y: stepsBottom - h, w: STEP_W, h };
}

export function bonusRect(side: number): Rect {
  const w = 184;
  const left = SENATE.x + (SENATE.w - (2 * w + 12)) / 2;
  return { x: left + side * (w + 12), y: stepsBottom + 18, w, h: 56 };
}

// ---------------------------------------------------------------------------
// The tableau: the mat, and everything the seat on show holds
// ---------------------------------------------------------------------------

/** Where the mat's own coordinates start on the table. */
export const MAT_AT: Point = { x: TABLEAU.x + 8, y: TABLEAU.y + 6 };
export const MAT: Rect = { x: MAT_AT.x, y: MAT_AT.y, w: MAT_W, h: MAT_H };

const onTable = (p: Point): Point => ({ x: MAT_AT.x + p.x, y: MAT_AT.y + p.y });

export function trayRect(i: number): Rect {
  const c = onTable(trayCentre(i));
  return { x: c.x - TRAY_W / 2, y: c.y - TRAY_H / 2, w: TRAY_W, h: TRAY_H };
}

export function slotRect(i: number): Rect {
  const c = onTable(slotCentre(i));
  return { x: c.x - SLOT_W / 2, y: c.y - SLOT_H / 2, w: SLOT_W, h: SLOT_H };
}

/** The `k`th of `count` markers in one row of tray `tray`, as a box on the table. */
export function markerRect(tray: number, k: number, count: number, row: MarkerRow = 'only'): Rect {
  const c = onTable(trayRow(tray, count, row).centre(k));
  const size = MARKER + 4;
  return { x: c.x - size / 2, y: c.y - size / 2, w: size, h: size };
}

/** The extra-action space of an action: a tab on the top right edge of its tray. */
export function plus2Rect(tray: number): Rect {
  const r = trayRect(tray);
  return { x: r.x + r.w - 27, y: r.y - 7, w: 24, h: 14 };
}

const RIGHT = MAT.x + MAT.w + 14;
const RIGHT_END = TABLEAU.x + TABLEAU.w - 12;

export const VP_BADGE: Rect = { x: RIGHT, y: TABLEAU.y + 8, w: 76, h: 76 };
export const SUPPLY: Rect = { x: RIGHT + 86, y: TABLEAU.y + 12, w: 86, h: 68 };
export const LEGION_CAMP: Rect = { x: RIGHT + 180, y: TABLEAU.y + 12, w: 108, h: 68 };
export const WORKER_CAMP: Rect = { x: RIGHT + 296, y: TABLEAU.y + 12, w: 108, h: 68 };
/** Bonus tiles held, along the top right. */
export const BONUS_ROW: Rect = {
  x: RIGHT + 416,
  y: TABLEAU.y + 12,
  w: RIGHT_END - (RIGHT + 416),
  h: 68,
};

export const HELD_TILE = 42;
/** Forum tiles and kept Trajan demand tiles. */
export const TILES_ROW: Rect = { x: RIGHT, y: TABLEAU.y + 122, w: 392, h: HELD_TILE };
/** Extra-action tiles. */
export const EXTRA_ROW: Rect = { x: RIGHT + 404, y: TABLEAU.y + 122, w: 176, h: HELD_TILE };
/** Construction tiles, grouped by building. */
export const BUILT_ROW: Rect = {
  x: RIGHT + 592,
  y: TABLEAU.y + 122,
  w: RIGHT_END - (RIGHT + 592),
  h: HELD_TILE,
};

export const CARD_W = 64;
export const CARD_H = 92;
export const HAND: Rect = { x: RIGHT, y: TABLEAU.y + 196, w: 580, h: CARD_H + 16 };
export const DISPLAY: Rect = {
  x: RIGHT + 592,
  y: TABLEAU.y + 196,
  w: RIGHT_END - (RIGHT + 592),
  h: CARD_H + 16,
};

/** Positions along a row that pack closer as it fills, so a row never spills out of its box. */
export function rowItem(row: Rect, k: number, of: number, itemW: number, gap = 6): Rect {
  const pitch = packPitch(of, row.w - itemW, itemW + gap);
  return { x: row.x + k * pitch, y: row.y, w: itemW, h: row.h };
}

export const handCard = (k: number, of: number): Rect => ({
  ...rowItem({ ...HAND, y: HAND.y + 12, h: CARD_H }, k, of, CARD_W),
  h: CARD_H,
});

export const displayCard = (k: number, of: number): Rect => ({
  ...rowItem({ ...DISPLAY, x: DISPLAY.x + 6, w: DISPLAY.w - 12, y: DISPLAY.y + 12 }, k, of, CARD_W),
  h: CARD_H,
});

/** The Trajan demand tiles come after the forum tiles, in the same row. */
export function heldTiles(p: PlayerView): { id: string; rect: Rect }[] {
  const ids = [...p.forumTiles.map((t) => t.id), ...p.trajanDemandTiles.map((t) => t.id)];
  return ids.map((id, k) => ({ id, rect: rowItem(TILES_ROW, k, ids.length, HELD_TILE) }));
}

export function extraTiles(p: PlayerView): { id: string; rect: Rect }[] {
  return p.extraActionTiles.map((t, k) => ({
    id: t.id,
    rect: rowItem(EXTRA_ROW, k, p.extraActionTiles.length, HELD_TILE),
  }));
}

/** One stack per building type, in the order the manifest lists them. */
export function builtStacks(p: PlayerView): { icon: string; ids: string[]; rect: Rect }[] {
  const icons = [...new Set(MANIFESTS.tiles.construction.map((t) => t.icon))];
  const held = icons
    .map((icon) => ({
      icon,
      ids: p.constructionTiles.filter((t) => t.icon === icon).map((t) => t.id),
    }))
    .filter((g) => g.ids.length > 0);
  return held.map((g, k) => ({ ...g, rect: rowItem(BUILT_ROW, k, held.length, HELD_TILE + 4) }));
}

export const BONUS_CHIP = 54;
export function bonusTiles(p: PlayerView): { id: string; rect: Rect }[] {
  return p.bonusTiles.map((t, k) => ({
    id: t.id,
    rect: {
      ...rowItem(BONUS_ROW, k, p.bonusTiles.length, BONUS_CHIP, 8),
      y: BONUS_ROW.y + 12,
      h: BONUS_CHIP,
    },
  }));
}

// ---------------------------------------------------------------------------
// Everything at once
// ---------------------------------------------------------------------------

/** A marker the mat has laid out somewhere other than at rest: in placement or arranging. */
export interface MarkerSpot {
  index: number;
  tray: number;
  /** Its place in its row of that tray, how many share the row, and which row. */
  k: number;
  count: number;
  row: MarkerRow;
}

/**
 * Every object on the table that can be pointed at, live or not, keyed by the
 * name the rest of the client uses for it. `seat` is whose tableau is on show;
 * `markers` are the mat's movable markers, when it has any.
 */
export function tableLayout(
  view: View,
  seat: PlayerId,
  markers: readonly MarkerSpot[] = [],
): Map<TargetId, Rect> {
  const rects = new Map<TargetId, Rect>();
  const put = (id: TargetId, r: Rect) => rects.set(id, r);

  put('time', TIME_TRACK);
  for (let i = 0; i < 3; i++) put(`demand:${i}`, demandSlot(i));
  view.board.forum.forEach((_, i) => put(`forum:${i}`, forumSpace(i)));
  view.board.yellowSpaces.forEach((_, i) => put(`yellow:${i}`, yellowSpace(i)));
  for (const p of MANIFESTS.provinces) put(`province:${p.id}`, provinceRect(p.id));
  put('italia', provinceRect('italia'));
  const rows = siteRows(view.playerCount);
  view.board.sites.forEach((_, i) => put(`site:${i}`, siteRect(i, rows)));
  view.board.trajanStacks.forEach((_, i) => put(`stack:${i}`, stackRect(i)));
  MANIFESTS.senateTrack.spaces.forEach((_, i) => put(`senate:${i}`, senateStep(i)));
  view.board.senateBonus.forEach((_, i) => put(`bonus:${i}`, bonusRect(i)));
  view.board.ships.forEach((s, i) => put(`ship:${s.id}`, shipRect(i)));
  put('deck', DECK);
  view.discards.forEach((_, i) => put(`pile:${i}`, pileRect(i)));

  for (let i = 0; i < 6; i++) {
    put(`tray:${i}`, trayRect(i));
    put(`slot:${i}`, slotRect(i));
    put(`plus2:${MANIFESTS.trayActions[i]!}` as TargetId, plus2Rect(i));
  }
  for (const m of markers) put(`marker:${m.index}`, markerRect(m.tray, m.k, m.count, m.row));

  const p = view.players[seat];
  if (p) {
    put('vp', VP_BADGE);
    put('supply', SUPPLY);
    put('camp:legion', LEGION_CAMP);
    put('camp:workers', WORKER_CAMP);
    for (const t of heldTiles(p)) put(`held:${t.id}`, t.rect);
    for (const t of extraTiles(p)) put(`held:${t.id}`, t.rect);
    for (const g of builtStacks(p)) put(`held:${g.ids[0]!}`, g.rect);
    for (const t of bonusTiles(p)) put(`held:${t.id}`, t.rect);
    const hand = p.hand ?? [];
    hand.forEach((c, k) => put(`card:${c.id}`, handCard(k, hand.length)));
    put('display', DISPLAY);
    p.display.forEach((c, k) => put(`card:${c.id}`, displayCard(k, p.display.length)));
  }
  return rects;
}

/** A rectangle as percentages of the table, for laying HTML over the canvas. */
export function percentOf(r: Rect): { left: string; top: string; width: string; height: string } {
  const pct = (n: number, of: number) => `${Math.round((n / of) * 100000) / 1000}%`;
  return {
    left: pct(r.x, TABLE_W),
    top: pct(r.y, TABLE_H),
    width: pct(r.w, TABLE_W),
    height: pct(r.h, TABLE_H),
  };
}
